// Global Instructions Rule Applied!
//
// Navbar stage-alerts bell (Phase 6, mockup 6.1 signed off): active
// stage-aging alerts visible to the signed-in user's email, grouped by
// pipeline (crit before warn, most days first — the RPC pre-sorts). Badge =
// UNREAD count. Clicking a row marks it read and opens the deal form —
// Execution tab for execution-pipeline alerts. Rows are produced by the
// hourly scan and live-filtered server-side; the bell refetches on mount,
// every 5 minutes, and whenever the panel opens.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Badge, Dropdown, Empty, Spin } from 'antd'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBell, faClock } from '@fortawesome/free-solid-svg-icons'
import { supabase } from '../../../core/lib/supabase'
import { PIPELINE_TYPE_DEAL_EXECUTION } from '../../../state_manager/globals.ts'
import { DEFAULT_WARN_COLOUR, DEFAULT_CRIT_COLOUR } from '../../../core/utils/pipelineAge.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { StageAlertModel } from '../../../state_manager/data_models/stage_alerts_model.ts'
import { fetchStageAlerts, markStageAlertsRead } from '../services/stageAlertsService.ts'

const POLL_MS = 5 * 60 * 1000

// The bell polls unattended, so a token snapshot (Redux) can be expired by
// the time the interval fires (throttled refresh timers in backgrounded
// tabs). getSession() refreshes an expired token before returning it.
const freshToken = async (): Promise<string | null> => {
  const {
    data: { session }
  } = await supabase.auth.getSession()
  return session?.access_token || null
}

/** Same visual as the kanban DealAgeChips chip (colour = cfg band colour,
 *  falling back to the app-wide warn/crit defaults). */
function AgeChip({ alert }: { alert: StageAlertModel }): React.ReactElement {
  const colour =
    alert.stage_alerts__colour ||
    (alert.stage_alerts__severity === 'crit' ? DEFAULT_CRIT_COLOUR : DEFAULT_WARN_COLOUR)
  return (
    <span
      title={`${alert.stage_alerts__days} day${alert.stage_alerts__days === 1 ? '' : 's'} in stage`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        fontSize: 11,
        fontWeight: 500,
        lineHeight: '14px',
        padding: '1px 5px',
        borderRadius: 6,
        whiteSpace: 'nowrap',
        backgroundColor: `${colour}1A`,
        color: colour,
        border: `1px solid ${colour}33`,
        flexShrink: 0
      }}
    >
      <FontAwesomeIcon icon={faClock} style={{ fontSize: 9 }} />
      {alert.stage_alerts__days}d
    </span>
  )
}

export default function StageAlertsBell(): React.ReactElement | null {
  const { isAuthenticated } = useSelector((state: { auth: { isAuthenticated: boolean } }) => state.auth)
  const { openFormInTab } = useTabManagerContext()
  const [alerts, setAlerts] = useState<StageAlertModel[]>([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const fetchedOnce = useRef(false)

  const load = useCallback(async () => {
    const accessToken = await freshToken()
    if (!accessToken) return
    setLoading(true)
    try {
      const result = await fetchStageAlerts(accessToken)
      if (result.data) {
        setAlerts(result.data.alerts)
        setUnread(result.data.unread_count)
      }
    } finally {
      setLoading(false)
      fetchedOnce.current = true
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) return
    load()
    const timer = setInterval(load, POLL_MS)
    return () => clearInterval(timer)
  }, [isAuthenticated, load])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next)
      if (next) load()
    },
    [load]
  )

  const markRead = useCallback(async (alertIds: string[] | null) => {
    setAlerts((prev) =>
      prev.map((a) =>
        alertIds === null || alertIds.includes(a.stage_alerts__id) ? { ...a, stage_alerts__read: true } : a
      )
    )
    setUnread((prev) => (alertIds === null ? 0 : Math.max(0, prev - alertIds.length)))
    await markStageAlertsRead(alertIds, await freshToken())
  }, [])

  const openAlert = useCallback(
    (alert: StageAlertModel) => {
      if (!alert.stage_alerts__read) markRead([alert.stage_alerts__id])
      setOpen(false)
      const isExecution = alert.pipelines__type === PIPELINE_TYPE_DEAL_EXECUTION
      openFormInTab(
        'deals',
        alert.deals__id,
        alert.deals__name || 'Deal',
        isExecution ? { tab: 'execution' } : null
      )
    },
    [markRead, openFormInTab]
  )

  // Group by pipeline, preserving the RPC's ordering.
  const groups = useMemo(() => {
    const byPipeline = new Map<string, { name: string; rows: StageAlertModel[] }>()
    alerts.forEach((a) => {
      const key = a.pipelines__id
      if (!byPipeline.has(key)) byPipeline.set(key, { name: a.pipelines__name || 'Pipeline', rows: [] })
      byPipeline.get(key)!.rows.push(a)
    })
    return Array.from(byPipeline.values())
  }, [alerts])

  if (!isAuthenticated) return null

  const panel = (
    <div
      className="bg-white rounded-lg shadow-xl border border-gray-100"
      style={{ width: 400, maxHeight: 480, display: 'flex', flexDirection: 'column' }}
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
        <span className="font-semibold text-sm text-gray-900">Stage Alerts</span>
        {unread > 0 && (
          <span
            className="text-xs text-blue-500 cursor-pointer hover:underline"
            onClick={() => markRead(null)}
          >
            Mark all read
          </span>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading && !fetchedOnce.current ? (
          <div className="flex justify-center py-8">
            <Spin size="small" />
          </div>
        ) : groups.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No stage alerts" className="py-6" />
        ) : (
          groups.map((group) => (
            <div key={group.name}>
              <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 bg-gray-50">
                {group.name}
              </div>
              {group.rows.map((alert) => (
                <div
                  key={alert.stage_alerts__id}
                  className="flex items-start gap-2.5 px-4 py-2 border-b border-gray-50 cursor-pointer hover:bg-blue-50/50"
                  onClick={() => openAlert(alert)}
                >
                  <span
                    className="mt-1.5 rounded-full flex-shrink-0"
                    style={{
                      width: 7,
                      height: 7,
                      backgroundColor: alert.stage_alerts__read ? 'transparent' : '#1890ff'
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-blue-600 truncate text-[13px] ${
                          alert.stage_alerts__read ? '' : 'font-semibold'
                        }`}
                      >
                        {alert.deals__name || 'Unnamed deal'}
                      </span>
                      <AgeChip alert={alert} />
                    </div>
                    <div className="text-xs text-gray-400 truncate">
                      {[alert.accounts__name, alert.pipeline_stages__name].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <div className="px-4 py-1.5 text-center text-[11px] text-gray-300 border-t border-gray-100">
        Alerts refresh hourly from stage aging thresholds
      </div>
    </div>
  )

  return (
    <Dropdown
      open={open}
      onOpenChange={handleOpenChange}
      trigger={['click']}
      placement="bottomRight"
      dropdownRender={() => panel}
    >
      <span
        className="relative cursor-pointer inline-flex items-center"
        data-debug="stage-alerts-bell"
        style={{ padding: '2px 2px' }}
      >
        <Badge count={unread} size="small" overflowCount={99} offset={[2, -2]}>
          <FontAwesomeIcon icon={faBell} className="text-white text-base" />
        </Badge>
      </span>
    </Dropdown>
  )
}
